import React, { useState } from 'react';
import { Calculator, Copy, Trash2, DollarSign } from 'lucide-react';

const SimpleTaxCalculatorTool = () => {
  const [income, setIncome] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  const calculate = () => {
    const incomeValue = parseFloat(income);
    const rateValue = parseFloat(taxRate);

    if (isNaN(incomeValue) || isNaN(rateValue) || incomeValue < 0 || rateValue < 0 || rateValue > 100) {
      setResult(null);
      return;
    }

    const taxAmount = (incomeValue * rateValue) / 100;
    const netIncome = incomeValue - taxAmount;
    const effectiveRate = rateValue;

    setResult({
      income: incomeValue,
      taxRate: rateValue,
      taxAmount,
      netIncome,
      effectiveRate
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setIncome('');
    setTaxRate('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Income ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={income}
              onChange={(e) => setIncome(e.target.value)}
              placeholder="e.g., 50000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="income-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Tax Rate (%)
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={taxRate}
              onChange={(e) => setTaxRate(e.target.value)}
              placeholder="e.g., 25"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="tax-rate-input"
            />
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-tax-btn"
      >
        <Calculator size={18} />
        Calculate Tax
      </button>

      {/* Results Section */}
      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4" data-testid="tax-results">
          {/* Main Results */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Tax Amount</span>
                <button
                  onClick={() => copyResult(result.taxAmount, 'tax')}
                  className={`p-1.5 rounded transition-colors ${
                    copied === 'tax' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-tax"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-red-400" data-testid="tax-amount-result">
                {formatCurrency(result.taxAmount)}
              </div>
            </div>

            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Net Income (After Tax)</span>
                <button
                  onClick={() => copyResult(result.netIncome, 'net')}
                  className={`p-1.5 rounded transition-colors ${
                    copied === 'net' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-net"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-green-400" data-testid="net-income-result">
                {formatCurrency(result.netIncome)}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="border-t border-gray-700 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign size={16} className="text-blue-400" />
              <h4 className="text-sm font-medium text-gray-300">Calculation Summary</h4>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Gross Income</span>
                <span className="text-white font-medium">{formatCurrency(result.income)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Tax Rate</span>
                <span className="text-white font-medium">{result.taxRate}%</span>
              </div>
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">Tax Amount (income × rate ÷ 100)</span>
                <span className="text-red-400 font-medium">{formatCurrency(result.taxAmount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Net Income (income - tax)</span>
                <span className="text-green-400 font-medium">{formatCurrency(result.netIncome)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Common Tax Rates Info */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Common Tax Brackets (Reference)</h3>
        <div className="grid md:grid-cols-4 gap-4 text-sm text-gray-400">
          <div>
            <div className="text-gray-500 mb-1">Low</div>
            <div>10-15%</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Medium</div>
            <div>22-24%</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">High</div>
            <div>32-35%</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Top</div>
            <div>37%+</div>
          </div>
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Note</h3>
        <div className="text-sm text-gray-400">
          This is a simple flat-rate tax calculator. Actual taxes may vary based on progressive tax brackets, deductions, credits, and local regulations. Consult a tax professional for accurate tax planning.
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-tax-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default SimpleTaxCalculatorTool;
